import { InfoComponent } from "@lxp-app/components/atoms/infoComponent";

interface ObjectiveFibQuestionProps {
  problemStatement: string;
  updateAnswerKeyHandler: (answerKey: Record<string, string>) => void;
  answerKey: Record<string, string> | undefined;
  caseSensitive?: boolean;
}

const sanitizeInput = (input: string) => {
  return input.replace(/</g, "&lt;").replace(/>/g, "&gt;");
};

const deSanitizeInput = (input: string) => {
  if (input?.length > 0)
    return input.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
};

const ObjectiveFibQuestion = ({
  answerKey,
  problemStatement,
  updateAnswerKeyHandler,
  caseSensitive
}: ObjectiveFibQuestionProps) => {
  const blanks = (problemStatement.match(/&blank\d+/g) || []).map((blank) => {
    let modifiedBlank = blank.replace("&", "");
    return { [modifiedBlank]: modifiedBlank.replace("blank", "blank ") };
  });

  const values = answerKey === undefined || answerKey === null ? {} : answerKey;

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    blank: string
  ) => {
    const value = event.target.value;
    const newValue = { ...values, [blank]: sanitizeInput(value) };
    updateAnswerKeyHandler(newValue);
  };

  const updateProblemText = () => {
    let updatedText = problemStatement;

    blanks.forEach((blank) => {
      const blankKey = Object.keys(blank)[0];
      const value = values[blankKey];
      if (value && value.trim().length > 0) {
        updatedText = updatedText.replace(
          `&${blankKey};`,
          `<span class="fib-blank inline-block border-b-2 border-gray-800 dark:border-gray-200 min-w-[96px] sm:min-w-[150px] align-middle mx-0.5 pb-0.5">${sanitizeInput(value)}</span>`
        );
      } else {
        updatedText = updatedText.replace(
          `&${blankKey};`,
          `<span class="fib-blank inline-block border-b-2 border-gray-800 dark:border-gray-200 min-w-[96px] sm:min-w-[150px] align-middle mx-0.5 h-[22px]">&nbsp;</span>`
        );
      }
    });

    return updatedText;
  };

  const renderInputs = () => {
    return blanks.map((blank) => {
      const blankKey = Object.keys(blank)[0];
      const blankVal = Object.values(blank)[0];
      return (
        <div key={blankKey}>
          <input
            type="text"
            id={blankKey}
            placeholder={`Write answer for ${blankVal}`}
            value={deSanitizeInput(values[blankKey]) || ""}
            onChange={(event) => handleInputChange(event, blankKey)}
            autoComplete="off"
            className="border border-primary/50 p-3 rounded-md focus:border-primary dark:bg-bkg dark:border-secondary/20 dark:focus:border-secondary/50 focus:ring-transparent w-full max-w-full"
          />
        </div>
      );
    });
  };

  return (
    <div>
      <div
        dangerouslySetInnerHTML={{ __html: updateProblemText() }}
        className="mb-10 text-xl font-medium no-select"
        style={{
          whiteSpace: "pre-line",
          lineHeight: "2.2em",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none"
        }}
      />
      {caseSensitive && (
        <InfoComponent
          textSize={"base"}
          iconSize={5}
          iconVariant={"outline"}
          text={
            "This question is case sensitive so please enter your answers using the exact uppercase and lowercase letters."
          }
          iconPosition={"center"}
        />
      )}
      <div className="mt-5 space-y-6 md:space-y-10 w-full max-w-full">
        {renderInputs()}
      </div>
    </div>
  );
};

export default ObjectiveFibQuestion;
